import { Box, FormHelperText, Skeleton, Stack, Switch, SwitchProps, Typography } from "@mui/material";
import { Controller, FieldValues, Path, UseFormReturn } from "react-hook-form";
import CheckedIndicator from "../common/CheckedIndicator";
import FormFieldHint from "./FormFieldHint";

interface Props<T extends FieldValues> extends Omit<SwitchProps, "name" | "label" | "control" | "form"> {
	name: Path<T>;
	label: string;
	readOnly?: boolean;
	isLoading?: boolean;
	hintMessage?: string;
	afterChange?: () => void;
	form: UseFormReturn<T, null>;
}

/**
 * FormSwitchField acts as a wrapper component for switch fields used in conjunction with React Hook Form.
 *
 * @param name        the name of the field bound to this text field
 * @param label       the label of the text field
 * @param readOnly    if true, makes the field read-only
 * @param isLoading   if true, the component will render a skeleton element
 * @param hintMessage the optional hint message that may be rendered at the end of the field
 * @param form        the form reference
 * @param afterChange if defined, this callback is invoked right after internal onChange event
 * @param rest
 * @returns           a TextField wrapped in a React Hook Form Control component
 */
const FormSwitchField = <T extends FieldValues>({
	name,
	label,
	readOnly,
	isLoading,
	form,
	hintMessage,
	afterChange,
	...rest
}: Props<T>): React.ReactElement => {
	if (isLoading) {
		return <Skeleton height="56px" variant="rounded" className="loading-field" />;
	}

	return (
		<Controller
			name={name}
			control={form.control}
			render={({ field: { onChange, value }, fieldState: { error } }) => (
				<Stack spacing={1} direction="column">
					<Stack spacing={1} direction="row" alignItems="center" className="form-switch-container">
						<Typography>{label}</Typography>
						<Box flexGrow={1} />
						{(rest.disabled || readOnly) && <CheckedIndicator flag={value} />}

						{!rest.disabled && !readOnly && (
							<Switch
								{...rest}
								checked={value}
								onChange={(v) => {
									onChange(v.target.checked);
									afterChange?.();
								}}
							/>
						)}
					</Stack>
					<FormHelperText component="div" error={error !== undefined}>
						{error?.message ?? <FormFieldHint text={hintMessage} />}
					</FormHelperText>
				</Stack>
			)}
		/>
	);
};

export default FormSwitchField;
